/**
 * IDOR (Insecure Direct Object Reference) Protection Helper
 * Centralized helper to reduce code duplication across services
 * 
 * SECURITY: This helper ensures consistent IDOR protection across all services
 * - Validates userId matches authenticated user
 * - Logs unauthorized access attempts
 * - Throws consistent error messages
 */

import { auth } from '../../services/firebaseConfig';
import { AppError, ErrorCode } from '../errors';
import { sanitizeString } from '../../utils/sanitization';

export interface IDORProtectionOptions {
  action: string;
  resourceType?: string;
  customMessage?: string;
}

/**
 * Verify that userId matches the authenticated user
 * SECURITY: IDOR Protection - Prevents unauthorized access to user resources
 * 
 * @param userId - The user ID to verify (from user input/request)
 * @param options - Options for error messages and logging
 * @returns The authenticated user's UID
 * @throws AppError if user is not authenticated or userId doesn't match
 */
export function verifyUserIdOwnership(
  userId: string,
  options: IDORProtectionOptions
): string {
  // SECURITY: Validate userId format
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0 || userId.length > 1500) {
    throw new AppError({
      code: ErrorCode.VALIDATION_FAILED,
      domain: 'validation',
      message: 'Invalid user ID'
    });
  }

  // SECURITY: IDOR Protection - Verify userId matches authenticated user
  const currentUser = auth.currentUser;
  if (!currentUser) {
    // SECURITY: Sanitize action parameter to prevent XSS in error messages
    const sanitizedAction = sanitizeString(options.action, 100) || 'perform this action';
    throw new AppError({
      code: ErrorCode.AUTH_INVALID_CREDENTIALS,
      domain: 'auth',
      message: `User must be authenticated to ${sanitizedAction}`
    });
  }

  if (userId !== currentUser.uid) {
    // SECURITY: Log unauthorized access attempt
    // Note: Logging userId is safe for security monitoring
    // SECURITY: Removed console.warn to prevent information leakage
    
    // SECURITY: Sanitize user-provided options to prevent XSS in error messages
    const sanitizedAction = sanitizeString(options.action, 100) || 'access';
    const sanitizedResourceType = options.resourceType 
      ? sanitizeString(options.resourceType, 50) || 'resources'
      : 'resources';
    const sanitizedCustomMessage = options.customMessage 
      ? sanitizeString(options.customMessage, 500) 
      : null;
    
    throw new AppError({
      code: ErrorCode.DB_PERMISSION_DENIED,
      domain: 'firestore',
      message: sanitizedCustomMessage || `You can only ${sanitizedAction} your own ${sanitizedResourceType}`
    });
  }

  return currentUser.uid;
}

/**
 * Check if user is authenticated (non-throwing version)
 * @returns The authenticated user's UID or null if not authenticated
 */
export function getAuthenticatedUserId(): string | null {
  const currentUser = auth.currentUser;
  return currentUser?.uid || null;
}

/**
 * Require authentication (throwing version)
 * @param action - The action being performed (for error message)
 * @returns The authenticated user's UID
 * @throws AppError if user is not authenticated
 */
export function requireAuthentication(action: string): string {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    // SECURITY: Sanitize action parameter to prevent XSS in error messages
    const sanitizedAction = sanitizeString(action, 100) || 'perform this action';
    throw new AppError({
      code: ErrorCode.AUTH_INVALID_CREDENTIALS,
      domain: 'auth',
      message: `User must be authenticated to ${sanitizedAction}`
    });
  }
  return currentUser.uid;
}

